import { useEffect, useState } from "react";
import { TextInput } from "flowbite-react";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { useDebounce } from "../hooks";
import { useTranslation } from "react-i18next";

interface SearchInputProps {
  onSearch: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  className?: string;
  style?: React.CSSProperties;
  size?: "sm" | "md" | "lg";
  initialValue?: string;
}

const SearchInput: React.FC<SearchInputProps> = ({
  onSearch,
  placeholder = "Search...",
  debounceMs = 300,
  className = "",
  style = {},
  size = "sm",
  initialValue = "",
}) => {
  const [searchTerm, setSearchTerm] = useState<string | null>(initialValue || null);
  const debouncedSearchTerm = useDebounce(searchTerm, debounceMs);

  const {t} = useTranslation();

  useEffect(() => {
    if (debouncedSearchTerm !== null) {
      onSearch(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, onSearch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleClear = () => {
    setSearchTerm("");
    onSearch("");
  };

  return (
    <div className="relative">
      <TextInput
        type="text"
        value={searchTerm || ""}
        onChange={handleChange}
        placeholder={t("common.search", { placeholder })}
        icon={MagnifyingGlass}
        className={className}
        sizing={size}
        style={style}
      />

      <button
        type="button"
        onClick={handleClear}
        className={`absolute transition-all duration-300 h-[calc(100%-2px)] w-10 justify-center flex items-center rounded-r-lg right-[1px] top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-600 bg-white ${
          searchTerm ? "scale-100 z-10" : "scale-0 -z-10"
        }`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default SearchInput;
